import {createContext, type ReactNode, useContext, useMemo} from 'react';

import type {WorkstreamsViewPlace} from './workstreamsAnalytics';

/**
 * /workstreams: where a card sits, for its events — the page listing
 * it (`place`) and its section's key (`section`: 'dri', 'update_due',
 * 'enablement', 'nearest_team', 'pillar:<name>', 'archived', …). Cards outside
 * a sectioned list have neither.
 */
export type ScanCardOrigin = {place?: WorkstreamsViewPlace; section?: string};

const ScanCardOriginContext = createContext<ScanCardOrigin>({});

export const useScanCardOrigin = () => useContext(ScanCardOriginContext);

// The same two on the DOM, for the page's link handler (More details).
const SCAN_PLACE_ATTR = 'data-scan-place';
const SCAN_SECTION_ATTR = 'data-scan-section';

export const readScanCardOrigin = (element: Element): ScanCardOrigin => ({
  place: (element.closest(`[${SCAN_PLACE_ATTR}]`)?.getAttribute(SCAN_PLACE_ATTR) ?? undefined) as
    | WorkstreamsViewPlace
    | undefined,
  section: element.closest(`[${SCAN_SECTION_ATTR}]`)?.getAttribute(SCAN_SECTION_ATTR) ?? undefined,
});

/** Marks what's inside as the list on `place`. */
export const ScanPlaceOrigin = ({
  place,
  children,
}: {
  place?: WorkstreamsViewPlace;
  children: ReactNode;
}) => {
  const value = useMemo(() => ({place}), [place]);
  return (
    <ScanCardOriginContext.Provider value={value}>
      <div {...{[SCAN_PLACE_ATTR]: place}}>{children}</div>
    </ScanCardOriginContext.Provider>
  );
};

/** Marks what's inside as section `section` of the enclosing list. */
export const ScanSectionOrigin = ({section, children}: {section: string; children: ReactNode}) => {
  const {place} = useScanCardOrigin();
  const value = useMemo(() => ({place, section}), [place, section]);
  return (
    <ScanCardOriginContext.Provider value={value}>
      <div {...{[SCAN_SECTION_ATTR]: section}}>{children}</div>
    </ScanCardOriginContext.Provider>
  );
};
